import React, { useEffect, useState } from 'react'

// Components
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import BaseSignUpView, { BaseSignUpViewProps } from './BaseSignUpView'

// GraphQL
import {
  useGetRoleSignUpContentQuery,
  GetRoleSignUpContentQuery,
} from '@sketch/gql-types'

import { ProfessionalRole } from '@sketch/constants'

// Icons
import WorkspacesIcon from '@sketch/icons/workspaces-24.svg'
import ChatBubbleIcon from '@sketch/icons/chat-bubble-16.svg'
import InterfaceIcon from '@sketch/icons/interface-24.svg'

import { ImageToPreload, ImagesPreloader } from 'components/Image'

interface RoleSpecificSignUpViewProps
  extends OmitSafe<BaseSignUpViewProps, 'content'> {}

const localRoleToGqlRole: {
  [K in ProfessionalRole]: Exclude<
    keyof GetRoleSignUpContentQuery['roleSignUpContent'],
    '__typename'
  >
} = {
  DESIGNER: 'designer',
  DESIGN_SYSTEM_MANAGER: 'designSystemManager',
  MANAGER_OR_DESIGN_TEAM_LEAD: 'managerOrDesignTeamLead',
  PRODUCT: 'product',
  DEVELOPER: 'developer',
  MARKETING_SPECIALIST: 'marketingSpecialist',
  WRITER: 'writer',
  FINANCE_OR_ADMIN: 'financeOrAdmin',
  RESEARCHER: 'researcher',
  STUDENT: 'student',
  TEACHER: 'teacher',
  EDUCATIONAL_INSTITUTION: 'educationalInstitution',
  OTHER: 'other',
}

const getIconsToPreload = (
  data: GetRoleSignUpContentQuery | undefined
): ImageToPreload[] => {
  if (!data) {
    return []
  }

  return Object.values(localRoleToGqlRole).flatMap(role => {
    const { features } = data.roleSignUpContent[role]
    return features.map(({ icon }) => {
      return {
        src: icon.src,
        srcset: [],
      }
    })
  })
}

const RoleSpecificSignUpView: React.FC<RoleSpecificSignUpViewProps> = props => {
  const initialRole = props['professionalRole'] ?? 'OTHER'
  const [role, setRole] = useState<ProfessionalRole>(initialRole)
  const { data, loading, error } = useGetRoleSignUpContentQuery()

  useEffect(() => {
    const iconsToPreload = getIconsToPreload(data)
    // pre-load the icons for each role to prevent a delay when the user
    // switches role
    if (iconsToPreload.length === 0) {
      return
    }

    const resolveImageSrc = (s: string) => s
    const preloader = new ImagesPreloader(iconsToPreload, resolveImageSrc)
    preloader.whenReady().catch(() => {
      // When the component unmounts the promise to preload the images will be
      // cancelled by `dispose()` below.
      // To avoid an "unhandled rejection error" from bubbling up, we add this
      // catch and do nothing
    })

    return () => {
      preloader.dispose()
    }
  }, [data])

  if (loading) {
    return <DynamicLoadingPage />
  }

  if (error || !data) {
    // If an unexpected error occurs when trying to fetch the role content,
    // fall back to showing default content rather than an error page which
    // would prevent the user from signing up
    return <BaseSignUpView content={defaultContent} {...props} />
  }

  const roleKey = localRoleToGqlRole[role]
  const content = data.roleSignUpContent[roleKey]

  return <BaseSignUpView content={content} onRoleChange={setRole} {...props} />
}

const defaultContent: BaseSignUpViewProps['content'] = {
  __typename: 'SignUpContent',
  title: 'Sketch is your all-in-one design toolkit',
  tint: 'ORANGE',
  introduction:
    'Sketch gives you all the tools you need for truly collaborative design. From early ideas to pixel-perfect artwork, playable prototypes and handoff, it all starts here.',
  features: [
    {
      __typename: 'SignUpFeature',
      description: 'Get access to our native Mac app and Workspaces',
      icon: {
        __typename: 'SignUpImage',
        src: InterfaceIcon,
        alt: 'interface icon',
      },
    },
    {
      __typename: 'SignUpFeature',
      description: 'Online Workspace to store and share your work',
      icon: {
        __typename: 'SignUpImage',
        src: WorkspacesIcon,
        alt: 'workspaces icon',
      },
    },
    {
      __typename: 'SignUpFeature',
      description: 'Powerful sharing and collaboration tools',
      icon: {
        __typename: 'SignUpImage',
        src: ChatBubbleIcon,
        alt: 'chat bubble icon',
      },
    },
  ],
  callToAction: 'Sign Up',
  relatedContent: null,
  heroImage: null,
}

export default RoleSpecificSignUpView
